import { useState, useEffect } from 'react';
import { createStage, STAGE_WIDTH } from '../gameHelpers';
import { Player, Stage, Cell } from '../types';

// Hook to manage stage state and row clearance
export const useStage = (
  player: Player,
  resetPlayer: () => void
): [Stage, React.Dispatch<React.SetStateAction<Stage>>, number, boolean] => {
  const [stage, setStage] = useState<Stage>(createStage());
  const [rowsCleared, setRowsCleared] = useState(0);
  const [shouldCheckGameOver, setShouldCheckGameOver] = useState(false);

  useEffect(() => {
    setRowsCleared(0);

    const sweepRows = (newStage: Stage): Stage =>
      newStage.reduce<Stage>((ack, row) => {
        // if no empty cells, clear the row
        if (row.every(cell => cell[0] !== 0)) {
          setRowsCleared(prev => prev + 1);
          ack.unshift(Array.from({ length: STAGE_WIDTH }, (): Cell => [0, 'clear']));
          return ack;
        }
        ack.push(row);
        return ack;
      }, []);

    const updateStage = (prevStage: Stage): Stage => {
      // First create a deep copy of the previous stage
      const newStage = prevStage.map(row =>
        row.map(cell =>
          cell[1] === 'clear'
            ? ([0, 'clear'] as Cell)
            : [...cell] as Cell
        )
      );

      // Draw the current tetromino
      player.tetromino.forEach((row, y) => {
        row.forEach((value, x) => {
          if (value !== 0) {
            const newY = y + player.pos.y;
            const newX = x + player.pos.x;
            
            // Only draw within bounds
            if (
              newY >= 0 && newY < newStage.length &&
              newX >= 0 && newX < newStage[newY].length
            ) {
              // Always set the cell status based on player.collided
              newStage[newY][newX] = [
                value,
                player.collided ? 'merged' : 'clear',
              ];
            }
          }
        });
      });

      // Handle collision and reset player
      if (player.collided) {
        const clearedStage = sweepRows(newStage);
        
        // Check if the top rows (0-1) have any merged blocks
        // This indicates the game should be over
        const gameOverCondition = clearedStage[0].some(cell => cell[1] === 'merged') || 
                                  clearedStage[1].some(cell => cell[1] === 'merged');
        
        if (!gameOverCondition) {
          resetPlayer();
        } else {
          setShouldCheckGameOver(true);
        }
        
        return clearedStage;
      }
      
      return newStage;
    };

    setStage(prev => updateStage(prev));
  }, [player, resetPlayer]);

  // Reset shouldCheckGameOver after it's been read
  useEffect(() => {
    if (shouldCheckGameOver) {
      setShouldCheckGameOver(false);
    }
  }, [shouldCheckGameOver]);

  return [stage, setStage, rowsCleared, shouldCheckGameOver];
};